import { useEffect, useState } from "react";
import { Plus, Heart, Play, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { resolveFileUrl } from "@/components/FileDropzone";
import FileDropzone from "@/components/FileDropzone";
import { toast } from "sonner";

function CreateReelModal({ open, onClose, onCreated }) {
  const [caption, setCaption] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!videoUrl) {
      toast.error("Upload a video first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/reels", {
        caption,
        video_url: videoUrl,
        cover_url: coverUrl || null,
      });
      onCreated(data);
      onClose();
      setCaption("");
      setVideoUrl("");
      setCoverUrl("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FDFBF7] border-2 border-black w-full max-w-lg p-6"
        data-testid="reel-form"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="font-display font-black uppercase text-2xl">New reel</div>
          <button onClick={onClose} aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest mb-2">Video</div>
            <FileDropzone
              value={videoUrl}
              onChange={setVideoUrl}
              accept="video/*"
              label="Upload video"
              testId="reel-video-upload"
              aspect="aspect-[9/16] max-h-[380px]"
            />
          </div>
          <textarea
            placeholder="Caption (optional)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={2}
            data-testid="reel-caption"
            className="w-full px-3 py-2 bg-white border-2 border-black font-mono text-sm resize-none"
          />
        </div>
        <button
          onClick={submit}
          disabled={busy || !videoUrl}
          data-testid="reel-submit"
          className="mt-6 w-full py-3 bg-[#F7931A] border-2 border-black font-bold uppercase tracking-widest btn-brutal disabled:opacity-50"
        >
          {busy ? "..." : "Publish reel"}
        </button>
      </div>
    </div>
  );
}

function ReelCard({ reel, onLike }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div
      data-testid={`reel-${reel.id}`}
      className="relative border-2 border-black bg-black aspect-[9/16] overflow-hidden card-brutal"
    >
      <video
        src={resolveFileUrl(reel.video_url)}
        poster={reel.cover_url ? resolveFileUrl(reel.cover_url) : undefined}
        controls={playing}
        loop
        playsInline
        className="w-full h-full object-cover"
        onClick={(e) => {
          if (playing) return;
          setPlaying(true);
          e.currentTarget.play();
        }}
      />
      {!playing && (
        <button
          onClick={() => setPlaying(true)}
          className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors"
          aria-label="Play"
        >
          <div className="p-4 bg-[#F7931A] border-2 border-black">
            <Play className="w-8 h-8 fill-black" />
          </div>
        </button>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent text-white">
        <div className="font-display font-bold text-sm">{reel.author?.name}</div>
        {reel.caption && <div className="text-xs mt-1 opacity-90">{reel.caption}</div>}
      </div>
      <button
        onClick={() => onLike(reel)}
        data-testid={`reel-like-${reel.id}`}
        className={`absolute right-3 top-3 p-2 border-2 border-black backdrop-blur bg-white/90 ${
          reel.liked_by_me ? "text-[#F7931A]" : "text-black"
        }`}
      >
        <Heart className={`w-4 h-4 ${reel.liked_by_me ? "fill-current" : ""}`} />
        <div className="text-[10px] font-mono">{reel.like_count}</div>
      </button>
    </div>
  );
}

export default function Reels() {
  const [reels, setReels] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/reels").then((r) => setReels(r.data));
  }, []);

  const like = async (reel) => {
    try {
      const { data } = await api.post(`/reels/${reel.id}/like`);
      setReels((rs) =>
        rs.map((r) => (r.id === reel.id ? { ...r, ...data } : r))
      );
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 py-10">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-black text-4xl lg:text-5xl uppercase tracking-tighter">
            Reels
          </h1>
          <p className="mt-2 text-sm text-[#0A0A0A]/60 font-mono uppercase">
            Short videos from the network
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="btn-new-reel"
          className="inline-flex items-center gap-2 px-4 py-3 bg-[#F7931A] border-2 border-black font-bold uppercase tracking-widest text-sm btn-brutal"
        >
          <Plus className="w-4 h-4" />
          New reel
        </button>
      </div>

      {reels.length === 0 ? (
        <div className="border-2 border-black bg-white p-12 text-center font-mono text-sm text-[#0A0A0A]/60 uppercase tracking-widest">
          No reels yet. Post the first one.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {reels.map((r) => (
            <ReelCard key={r.id} reel={r} onLike={like} />
          ))}
        </div>
      )}

      <CreateReelModal open={open} onClose={() => setOpen(false)} onCreated={(r) => setReels((rs) => [r, ...rs])} />
    </main>
  );
}
